import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map } from 'rxjs';

import { MonthNames } from '@ninety/platform/models/_shared/month-names';
import { SegmentTrackEvent } from '@ninety/platform/models/analytics/segment/segment-track-event.enum';

import {
  InviteUsersActions,
  UserPreferencesActions,
  UserSettingsActions,
} from '../../../app-entities/users/users-state.actions';
import { AuthActions } from '../../auth/auth.actions';
import { CompanyActions } from '../../company/company-state.actions';

import { SegmentActions } from './segment.actions';

@Injectable()
export class AppGlobalAnalyticsEffects {
  private readonly actions$ = inject(Actions);

  trackFiscalCalendarChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyActions.changeFiscalCalendar),
      map(({ fiscalCalendar }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.CHANGED_FISCAL_CALENDAR,
          params: {
            monthValue: fiscalCalendar.startMonth,
            monthLabel: MonthNames[fiscalCalendar.startMonth],
          },
        })
      )
    )
  );

  trackUserInvites_sendInvite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InviteUsersActions.inviteUserToCompany),
      filter(({ sendInvite }) => sendInvite),
      map(({ invitedUser }) => {
        const params = { invitedUserEmail: invitedUser.email };
        return SegmentActions.track({ event: SegmentTrackEvent.INVITED_USER, params });
      })
    )
  );

  trackUserInvites_notExistingUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InviteUsersActions.inviteUserToCompany),
      filter(({ existingDirectoryUser }) => !existingDirectoryUser),
      map(({ invitedUser }) => {
        const params = { invitedUserEmail: invitedUser.email };
        return SegmentActions.track({ event: SegmentTrackEvent.ADDED_PERSON_TO_DIRECTORY, params });
      })
    )
  );

  trackThemeUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPreferencesActions.themeSaved),
      map(({ theme }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.THEME_CHANGED,
          params: { themeSelected: theme },
        })
      )
    )
  );

  trackNotificationEventsUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPreferencesActions.updateEventNotification),
      map(({ data }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.USER_EVENT_NOTIFICATION_CHANGED,
          params: { userNotificationSettingsArray: data },
        })
      )
    )
  );

  trackGoogleTaskIntegrationChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSettingsActions.googleTaskIntegrationToggled),
      map(({ enabled }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.GOOGLE_TASK_INTEGRATION_CHANGED,
          params: { enabled },
        })
      )
    )
  );

  trackMicrosoftTaskIntegrationChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSettingsActions.microsoftTaskIntegrationToggled),
      map(({ enabled }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.MICROSOFT_TASK_INTEGRATION_CHANGED,
          params: { enabled },
        })
      )
    )
  );

  trackMicrosoftTaskIntegrationTaskListChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserSettingsActions.microsoftTaskIntegrationTaskListChanged),
      map(() => SegmentActions.track({ event: SegmentTrackEvent.MICROSOFT_TASK_INTEGRATION_TASK_LIST_CHANGED }))
    )
  );

  trackLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logIn),
      map(() => SegmentActions.track({ event: SegmentTrackEvent.LOGIN }))
    )
  );

  trackABTodosSetting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyActions.updatedABTodosSetting),
      map(({ agreementBasedTodos }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.AGREEMENT_BASED_TODOS_CHANGED,
          params: { agreementBasedTodos },
        })
      )
    )
  );

  /** Which number a company said was its top priority when it moved to labels. Dispatched from the
   *  switch itself, so the event follows the write rather than the click that asked for it. */
  trackIssueRatingTopPrioritySelected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyActions.issueRatingTopPrioritySelected),
      map(({ topPriority, companyId }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.SETTINGS_ISSUE_RATING_SCALE_SELECTED,
          params: { entityId: companyId, topPriority },
        })
      )
    )
  );

  trackEosPureToggled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyActions.eosPureToggled),
      map(({ enabled }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.SETTINGS_EOS_PURE_TOGGLED,
          params: { enabled },
        })
      )
    )
  );

  trackCompanyIssueVotingToggled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyActions.issueVotingToggled),
      map(({ companyId, isVotingEnabled }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.SETTINGS_ISSUE_VOTING_TOGGLED,
          params: { entityId: companyId, isVotingEnabled },
        })
      )
    )
  );
}
